import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PACK_SIZES, calcShipping, MIN_PILLS, MAX_PILLS } from "./pharmacy";

const CartCtx = createContext(null);
const KEY = "blz_cart_v1";

function computeUnitPrice(product, packSize) {
  const salePerPill = Number(product.sale_price_per_pill || 0);
  if (salePerPill > 0) return Number((salePerPill * packSize).toFixed(2));
  const priceRaw = Number((product.price_per_pack || {})[String(packSize)] || 0);
  const disc = Number(product.discount_percent || 0) / 100;
  return Number((priceRaw * (1 - disc)).toFixed(2));
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (u) => u ? (u.startsWith("/") ? `${BACKEND}${u}` : u) : "";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
    loadedRef.current = true;
  }, []);
  useEffect(() => {
    if (!loadedRef.current) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, packSize, quantity = 1) => {
    if (!PACK_SIZES.includes(packSize)) return;
    const unitPrice = computeUnitPrice(product, packSize);
    setItems((prev) => {
      const key = `${product.id}::${packSize}`;
      const idx = prev.findIndex((i) => `${i.productId}::${i.packSize}` === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, {
        productId: product.id, name: product.name, image: resolveImage(product.image_url),
        generic: product.generic_name, strength: product.strength,
        packSize, quantity, unitPrice,
        prescriptionRequired: !!product.prescription_required,
      }];
    });
    setOpen(true);
  };
  const removeItem = (productId, packSize) =>
    setItems((prev) => prev.filter((i) => !(i.productId === productId && i.packSize === packSize)));
  const setQty = (productId, packSize, quantity) =>
    setItems((prev) => prev.map((i) =>
      i.productId === productId && i.packSize === packSize
        ? { ...i, quantity: Math.max(1, Math.min(20, quantity)) } : i));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const totalPills = items.reduce((s, i) => s + i.packSize * i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
    const shipping = calcShipping(totalPills);
    const grand = subtotal + shipping;
    let error = null;
    if (items.length > 0 && totalPills < MIN_PILLS)
      error = `Minimum order is ${MIN_PILLS} pills. Add ${MIN_PILLS - totalPills} more.`;
    if (totalPills > MAX_PILLS)
      error = `Maximum order is ${MAX_PILLS} pills. Remove ${totalPills - MAX_PILLS}.`;
    const requiresRx = items.some((i) => i.prescriptionRequired);
    return { totalPills, subtotal, shipping, grand, error, requiresRx };
  }, [items]);

  return (
    <CartCtx.Provider value={{ items, open, setOpen, addItem, removeItem, setQty, clear, totals }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
